import { createSlice, createAsyncThunk, PayloadAction, Draft } from '@reduxjs/toolkit';
import { Task, TaskFilters } from '../types/task';
import { tasksApi } from '../services/tasksApi';

interface TasksState {
  tasks: Task[];
  selectedTask: Task | null;
  filters: TaskFilters;
  loading: boolean;
  error: string | null;
  upcomingTasks: Task[];
}

const initialState: TasksState = {
  tasks: [],
  selectedTask: null,
  filters: {},
  loading: false,
  error: null,
  upcomingTasks: [],
};

// Async thunks
export const fetchTasks = createAsyncThunk(
  'tasks/fetchTasks',
  async (filters?: TaskFilters) => {
    const response = await tasksApi.getTasks(filters);
    return response.results;
  }
);

export const fetchTask = createAsyncThunk(
  'tasks/fetchTask',
  async (id: string) => {
    return await tasksApi.getTask(id);
  }
);

export const createTask = createAsyncThunk(
  'tasks/createTask',
  async (task: Partial<Task>) => {
    return await tasksApi.createTask(task);
  }
);

export const updateTask = createAsyncThunk(
  'tasks/updateTask',
  async ({ id, task }: { id: string; task: Partial<Task> }) => {
    return await tasksApi.updateTask(id, task);
  }
);

export const deleteTask = createAsyncThunk(
  'tasks/deleteTask',
  async (id: string) => {
    await tasksApi.deleteTask(id);
    return id;
  }
);

export const completeTask = createAsyncThunk(
  'tasks/completeTask',
  async (id: string) => {
    return await tasksApi.completeTask(id);
  }
);

export const archiveTask = createAsyncThunk(
  'tasks/archiveTask',
  async (id: string) => {
    return await tasksApi.archiveTask(id);
  }
);

export const fetchUpcomingTasks = createAsyncThunk(
  'tasks/fetchUpcomingTasks',
  async () => {
    return await tasksApi.getUpcomingTasks();
  }
);

const tasksSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    setFilters: (state: Draft<TasksState>, action: PayloadAction<TaskFilters>) => {
      state.filters = action.payload;
    },
    clearFilters: (state: Draft<TasksState>) => {
      state.filters = {};
    },
    setSelectedTask: (state: Draft<TasksState>, action: PayloadAction<Task | null>) => {
      state.selectedTask = action.payload;
    },
    clearError: (state: Draft<TasksState>) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch tasks
    builder
      .addCase(fetchTasks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = action.payload;
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch tasks';
      });

    // Fetch single task
    builder
      .addCase(fetchTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTask.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedTask = action.payload;
        // Update task in list if it exists
        const index = state.tasks.findIndex(t => t.id === action.payload.id);
        if (index >= 0) {
          state.tasks[index] = action.payload;
        } else {
          state.tasks.push(action.payload);
        }
      })
      .addCase(fetchTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch task';
      });

    // Create task
    builder
      .addCase(createTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTask.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks.unshift(action.payload); // Add to beginning
      })
      .addCase(createTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create task';
      });

    // Update task
    builder
      .addCase(updateTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTask.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.tasks.findIndex(t => t.id === action.payload.id);
        if (index >= 0) {
          state.tasks[index] = action.payload;
        }
        if (state.selectedTask?.id === action.payload.id) {
          state.selectedTask = action.payload;
        }
      })
      .addCase(updateTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update task';
      });

    // Delete task
    builder
      .addCase(deleteTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTask.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = state.tasks.filter(t => t.id !== action.payload);
        if (state.selectedTask?.id === action.payload) {
          state.selectedTask = null;
        }
      })
      .addCase(deleteTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete task';
      });

    // Complete task
    builder
      .addCase(completeTask.fulfilled, (state, action) => {
        const index = state.tasks.findIndex(t => t.id === action.payload.id);
        if (index >= 0) {
          state.tasks[index] = action.payload;
        }
        if (state.selectedTask?.id === action.payload.id) {
          state.selectedTask = action.payload;
        }
      });

    // Archive task
    builder
      .addCase(archiveTask.fulfilled, (state, action) => {
        const index = state.tasks.findIndex(t => t.id === action.payload.id);
        if (index >= 0) {
          state.tasks[index] = action.payload;
        }
        if (state.selectedTask?.id === action.payload.id) {
          state.selectedTask = null;
        }
      });

    // Fetch upcoming tasks
    builder
      .addCase(fetchUpcomingTasks.fulfilled, (state, action) => {
        state.upcomingTasks = action.payload;
      });
  },
});

export const { setFilters, clearFilters, setSelectedTask, clearError } = tasksSlice.actions;
export default tasksSlice.reducer;
